// QBRInsightsScreen.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { Button, Container, Typography } from "@mui/material";

import FiltersReport, {
    buildFiltersReportExcelSection,
} from "../components/FiltersReport";
import ReportBuilder, {
    CUSTOM,
} from "../components/shared/ReportBuilder/ReportBuilder";
import QBRInsightsMonthlyTable, {
    buildQBRInsightsMonthlyTableExcelSections,
} from "../components/QBRInsightsMonthlyTable";

import {
    DATATYPE_CHECKIN,
    DATATYPE_RESDATE,
    DEFAULT_COMPARISON,
    DISPLAY_ELEMENT_HELPER,
    PRESET_ANYTIME,
    PRESET_BOOKING_GROUP,
    PRESET_THIS_QUARTER,
} from "../helpers/filters";
import { TARGET_DAY } from "../helpers/targets";
import DataLoader from "../components/DataLoader";
import { useSelector } from "react-redux";
import { setTemplateKey } from "../actions/PDFExport";
import {
    buildMetricsSummaryExcelSections,
    REPORT_TABLE_METRICS_SUMMARY,
} from "../components/shared/ReportBuilder/ReportTableMetricsSummary";
import { buildExcelSectionsFromReports } from "../helpers/excelSections";
import { exportScreen } from "../helpers/export";

export const QBR_INSIGHTS_REPORT_KEY = "QBR_INSIGHTS";

const QBR_FILTERS_SHARED_PROPS = {
    BookingPresetOptions: [
        ...PRESET_BOOKING_GROUP,
        { value: PRESET_ANYTIME, label: "Lifetime" },
    ],
    rename: {},
    hide: [
        "ARRIVAL_PERIOD",
        "TIME_ARRIVAL_RANGE",
        "RESDATE_PERIOD",
        "TIME_BOOKING_RANGE",
    ],
    otherProps: {
        DATATYPE_VALUES: {
            [DATATYPE_RESDATE]: "Booked on",
            [DATATYPE_CHECKIN]: "Checked-in",
        },
    },
    showCustomTimePeriod: false,
};

export const getQBRInsightsReports = ({ hotelID, js, PDF = false }) => [
    ...(PDF
        ? [
              {
                  type: CUSTOM,
                  groupBy: [],
                  title: "Title",
                  skipExcelDataProcessing: true,
                  exportExcel: () => ({
                      id: "qbr-title",
                      kind: "title",
                      value: "Quarter Business Review Insights",
                  }),
                  options: { duplicable: false, disableInteraction: true },
                  render: () => (
                      <Typography variant="h4">
                          Quarter Business Review Insights
                      </Typography>
                  ),
              },
              {
                  type: CUSTOM,
                  groupBy: [],
                  title: "Filters",
                  skipExcelDataProcessing: true,
                  exportExcel: buildFiltersReportExcelSection,
                  options: { duplicable: false },
                  render: () => (
                      <FiltersReport
                          hideExports
                          {...QBR_FILTERS_SHARED_PROPS}
                      />
                  ),
              },
          ]
        : []),
    {
        type: REPORT_TABLE_METRICS_SUMMARY,
        groupBy: [],
        exportExcel: buildMetricsSummaryExcelSections,
        fieldsOrder: [
            "revenueTotal",
            "bookingsRooms",
            "ADRTotal",
            "sessionsTotal",
            "bookingEngineSessions",
            "bookingEngineSessionsRate",
            "perSessionValue",
            "perBESessionValue",
            "websiteConversionRate",
            "BEConversionRate",
            "bookingsMobile",
            "bookingsNotMobile",
            "revenueTotalMobile",
            "revenueTotalNotMobile",
            "revenueRoomMobile",
            "revenueRoomNotMobile",
        ],
        hideFields: Object.keys(DISPLAY_ELEMENT_HELPER).filter(
            (field) =>
                ![
                    "revenueTotal",
                    "bookingsRooms",
                    "ADRTotal",
                    "sessionsTotal",
                    "bookingEngineSessions",
                    "bookingEngineSessionsRate",
                    "perSessionValue",
                    "perBESessionValue",
                    "websiteConversionRate",
                    "BEConversionRate",
                    "bookingsMobile",
                    "bookingsNotMobile",
                    "revenueTotalMobile",
                    "revenueTotalNotMobile",
                    "revenueRoomMobile",
                    "revenueRoomNotMobile",
                ].includes(field),
        ),
        props: {
            hideEvents: true,
        },
    },
    {
        type: CUSTOM,
        groupBy: [],
        title: "Monthly actual vs target",
        skipExcelDataProcessing: true,
        exportExcel: buildQBRInsightsMonthlyTableExcelSections,
        options: { duplicable: false },
        props: {
            title: "Monthly actual vs target",
            addTotal: [
                "revenueTotal",
                "revenueRoom",
                "revenueRoomAndExtras",
                "bookingsRooms",
                "bookingsTotal",
                "sessionsTotal",
                "bookingEngineSessions",
            ],
        },
        render: ({ js }) => {
            const dateTypeDescription =
                js.dataType === DATATYPE_CHECKIN
                    ? "Checked-in uses check-in date, meaning the date when the stay starts"
                    : "Booked on uses reservation date, meaning the date when the booking was made";
            return (
                <QBRInsightsMonthlyTable
                    title="Monthly actual vs target"
                    description={`Breaks the selected period into months and compares each KPI against its monthly target. If there is no target for a metric or month, it falls back to previous year comparison. ${dateTypeDescription}. The Total column is only shown for metrics that can be summed.`}
                    fromDay={js.fromDay}
                    toDay={js.toDay}
                    PDF={PDF}
                />
            );
        },
    },
];

const QBRInsightsScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const js = useSelector((state) => state.globalReportFilters);
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const reports = getQBRInsightsReports({ hotelID, js });

    const onExcelExport = ({
        js: activeJs,
        hotelID: activeHotelID,
        activeFiltersRows,
        dataBookings,
        dataSimple,
        dataTargets,
    }) => {
        const { sections } = buildExcelSectionsFromReports({
            reports: getQBRInsightsReports({
                hotelID: activeHotelID,
                js: activeJs,
                PDF: true,
            }),
            js: activeJs,
            hotelID: activeHotelID,
            dataBookings,
            dataSimple,
            dataTargets,
            extraContext: { activeFiltersRows },
        });

        exportScreen({
            sections,
            worksheetName: "Quarter Business Review Insights",
            fileName: "QBR Insights.xlsx",
        });
    };

    return (
        <>
            <Container maxWidth={false}>
                <DataLoader Reservations SimpleData />
                <Typography variant="h4" sx={{ mt: 2, mb: 2 }}>
                    Quarter Business Review Insights
                </Typography>
                <Typography variant="subtitle1" color="initial">
                    QBR Report supports quarterly business reviews by enabling
                    monthly KPI target management and automated actuals
                    reporting.
                </Typography>
                <Typography variant="subtitle2" color="grey" fontSize="0.8em">
                    <b>Disclaimer:</b> BE Sessions should not be directly
                    compared with historical data, as the migration to Google
                    Analytics 4 (GA4) introduced changes to tracking
                    methodology, and Google continuously applies post-processing
                    to improve data quality.
                </Typography>
                <br />
                <FiltersReport
                    {...QBR_FILTERS_SHARED_PROPS}
                    onExcelExport={onExcelExport}
                    onPDFExport={() => {
                        dispatch(setTemplateKey(QBR_INSIGHTS_REPORT_KEY));
                        navigate("/pdfexport");
                    }}
                    defaultValues={{
                        dataType: DATATYPE_RESDATE,
                        presetDate: PRESET_THIS_QUARTER,
                        presetArrivalDate: PRESET_ANYTIME,
                        presetResDate: PRESET_ANYTIME,
                        hidePercentageFor: [],
                        filters: {
                            Cancellations: "included",
                            Target: TARGET_DAY,
                            Comparison: DEFAULT_COMPARISON,
                        },
                    }}
                    setProcessed={() => {}}
                    CustomFilters={() => (
                        <Button
                            variant="contained"
                            sx={{
                                height: "50px",
                                minHeight: "50px",
                            }}
                            onClick={() => {
                                navigate("/targets");
                            }}
                        >
                            Edit Targets
                        </Button>
                    )}
                />
                <ReportBuilder reports={reports} />
            </Container>
        </>
    );
};

export default QBRInsightsScreen;
